import { Injectable } from '@angular/core';
import { lastValueFrom, map } from 'rxjs';
import { Break, BreakType, PoolMode, PoolPeriod } from 'src/models';
import { ApiLimitParams, ApiService } from '../api.service';
import { buildMockBreaks, findMockBreak } from '../demo/demo-breaks.mock';
import { DemoService } from '../demo/demo.service';
import { PagedApiResult, PagingService } from '../paging/paging.service';

export interface GetBreaksOptions extends ApiLimitParams {
  convertId?: boolean;
  assigned?: boolean;
  filter?: string;
  'filter[name]'?: string;
  'filter[keyphrase]'?: string;
  'filter[id]'?: string;
  'deleted'?: 1 | 0;
  'noDeleted'?: 1 | 0;
  ignoreBreakLimit?: boolean;
}


@Injectable({ providedIn: 'root' })
export class BreakService {
  constructor(
    private api: ApiService,
    private paging: PagingService,
    private demo: DemoService,
  ) { }

  async getBreak(id: string): Promise<Break> {
    if (this.demo.isShowDemoDataActiveSnapshot()) {
      const item = findMockBreak(id);
      if (item) {
        return item;
      }
    }
    const response = await lastValueFrom(this.api.request<{ data: Break }>('get', `breaks/${id}`, null, null, true));
    return response.data;
  }

  getBreaks(breaksOptions?: GetBreaksOptions) {
    const { convertId, ...options } = breaksOptions || {};
    const limit = options?.limit || 1000;

    if (this.demo.isShowDemoDataActiveSnapshot()) {
      return this.paging.from(buildMockBreaks({ ...options, convertId }), limit);
    }

    return this.paging.getPagedResult<Break>(page =>
      this.api.request<PagedApiResult<Break>>('get', `breaks`, { sort: 'name', ignoreBreakLimit: true, ...options, page, limit }, null, true).pipe(map(result => {
        if (convertId) {
          return { ...result, data: result.data.map(x => ({ ...x, id: `break:${x.type}:${x.id}` })) };
        }
        return result;
      })), limit);
  }

  createBreak(name: string, type: BreakType, scope, access, breakLimit?: number, poolMode?: PoolMode, poolPeriod?: PoolPeriod) {
    if (this.demo.isShowDemoDataActiveSnapshot()) {
      return Promise.resolve({
        id: `demo-break-${Date.now()}`,
        name,
        type,
        active: true,
        public: true,
        archivedAt: '',
        scope,
        access: access || [],
        breakLimit,
        poolMode,
        poolPeriod,
      } as Break);
    }
    return lastValueFrom(
      this.api.request<{ data: Break }>('post', 'breaks', { name, type, scope, access, breakLimit, poolMode, poolPeriod }, null, true)
        .pipe(map(x => x.data)));
  }

  editBreak(id: string, break$: Partial<Break>) {
    if (this.demo.isShowDemoDataActiveSnapshot()) {
      return Promise.resolve({ data: { id, ...break$ } });
    }
    return lastValueFrom(this.api.request('put', `breaks/${id}`, break$, null, true));
  }

  deleteBreak(id: string) {
    if (this.demo.isShowDemoDataActiveSnapshot()) {
      return Promise.resolve({ data: { id } });
    }
    return lastValueFrom(this.api.request('delete', `breaks/${id}`, null, null, true));
  }

  archiveBreak(id: string) {
    if (this.demo.isShowDemoDataActiveSnapshot()) {
      return Promise.resolve({ data: { id } });
    }
    return lastValueFrom(this.api.request('delete', `breaks/${id}`, null, null, true));
  }
}
